import React from 'react';
import { Link } from 'react-router-dom';
import {
  FOOTER_SOLUTIONS,
  FOOTER_COMPANY,
  FOOTER_LEGAL,
  CONTACT_EMAIL,
  LEGACY_APP_URL,
  LEGACY_LOGIN_LABEL,
  MEMBER_DOOR_URL,
  MEMBER_SIGN_IN_URL,
  SUPPORT_EMAIL,
  type NavLink,
} from './marketingNav';
import { IOS_APP_URL, ANDROID_APP_URL } from '@/lib/desktopDownloads';
import { useMemberHelpLine } from '@/lib/memberHelpLine';

const LINK = 'text-sm text-gray-400 hover:text-v2accent';

const Column: React.FC<{ heading: string; links: NavLink[] }> = ({ heading, links }) => (
  <div>
    <h3 className="font-semibold text-white mb-4">{heading}</h3>
    <ul className="space-y-2">
      {links.map((link) => (
        <li key={link.to}>
          <Link to={link.to} className={LINK}>
            {link.label}
          </Link>
        </li>
      ))}
    </ul>
  </div>
);

interface MarketingFooterProps {
  onLogin?: () => void;
  onProviderDirectory?: () => void;
}

/** The public site's one footer. */
const MarketingFooter: React.FC<MarketingFooterProps> = ({ onLogin, onProviderDirectory }) => {
  const helpLine = useMemberHelpLine();
  return (
  <footer className="bg-gray-900 text-white py-12">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid md:grid-cols-5 gap-8 mb-8">
        <div className="md:col-span-2">
          <img src="/weavein-logo-2.png" alt="WeaveIn" className="h-16 w-auto mb-4 rounded" />
          <div className="flex items-center space-x-3 mb-4">
            <span className="text-gray-300 text-base">Powered by</span>
            <img src="/Trust-logo-white.png" alt="Trust" className="h-12 w-auto" />
          </div>
          <p className="text-sm text-gray-400 max-w-xs">
            One ledger. Every funder.
          </p>
          <p className="text-sm text-gray-400 mt-4">
            For a demo,{' '}
            <Link to="/contact" className="text-v2accent hover:underline">
              get in touch
            </Link>{' '}
            or write to{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} className="text-v2accent hover:underline">
              {CONTACT_EMAIL}
            </a>
            .
          </p>
        </div>

        <Column heading="Solutions" links={FOOTER_SOLUTIONS} />

        <div>
          <Column heading="Company" links={FOOTER_COMPANY} />
          <h3 className="font-semibold text-white mt-6 mb-4">Access</h3>
          <ul className="space-y-2">
            <li>
              <Link to="/member/home" className={LINK}>
                Member Portal
              </Link>
            </li>
            <li>
              {onLogin ? (
                <button onClick={onLogin} className={LINK}>
                  Staff Login
                </button>
              ) : (
                <Link to="/login" className={LINK}>
                  Staff Login
                </Link>
              )}
            </li>
            <li>
              <a href={LEGACY_APP_URL} className={LINK}>
                {LEGACY_LOGIN_LABEL} (app.trust.care)
              </a>
            </li>
            {onProviderDirectory && (
              <li>
                <button onClick={onProviderDirectory} className={LINK}>
                  Provider Directory
                </button>
              </li>
            )}
            <li>
              {/* Kept here, beside the other things only a customer needs.
                  It used to be the footer's "Contact" link, which sent people
                  with a sales question to the support desk. */}
              <a href={`mailto:${SUPPORT_EMAIL}`} className={LINK}>
                Customer Support
              </a>
            </li>
          </ul>
        </div>

        {/* TRUS-778: people looking for help for themselves. */}
        <div>
          <h3 className="font-semibold text-white mb-4">For individuals and families</h3>
          <ul className="space-y-2">
            <li>
              <Link to={MEMBER_DOOR_URL} className={LINK}>
                Get help
              </Link>
            </li>
            <li>
              <Link to={MEMBER_SIGN_IN_URL} className={LINK}>
                Member sign in
              </Link>
            </li>
            <li>
              <span className="text-sm text-gray-400">Get the app: </span>
              <a href={IOS_APP_URL} target="_blank" rel="noopener noreferrer" className={LINK}>
                App Store
              </a>
              <span className="text-sm text-gray-400"> · </span>
              <a href={ANDROID_APP_URL} target="_blank" rel="noopener noreferrer" className={LINK}>
                Google Play
              </a>
            </li>
            {helpLine && (
              <li>
                <span className="text-sm text-gray-400">Text or call </span>
                <a href={helpLine.tel} className={LINK}>
                  {helpLine.display}
                </a>
              </li>
            )}
          </ul>
        </div>
      </div>

      <div className="border-t border-gray-700 pt-8 flex flex-col md:flex-row justify-between items-center">
        <p className="text-sm text-gray-400">
          &copy; {new Date().getFullYear()} WeaveIn.ai by Trust. All rights reserved.
        </p>
        <div className="flex flex-wrap gap-x-6 gap-y-2 mt-4 md:mt-0">
          {FOOTER_LEGAL.map((link) => (
            <Link key={link.to} to={link.to} reloadDocument={link.to.endsWith('.html')} className={LINK}>
              {link.label}
            </Link>
          ))}
          <Link to="/contact" className={LINK}>
            Contact
          </Link>
        </div>
      </div>
    </div>
  </footer>
  );
};

export default MarketingFooter;
